import { LuCheck } from 'react-icons/lu';

import { type CheckboxProps, CheckboxSize, type CheckboxSizeType } from './types';
import { cn } from '../../utils/cn';
import { handleEventBehavior } from '../../utils/EventBehavior';

const sizeStyles = {
    [CheckboxSize.LARGE]: {
        box: 'h-6 w-6',
        font: 'text-sm',
        icon: 'h-4.5 w-4.5',
        padding: 'pt-0.5',
    },
    [CheckboxSize.MEDIUM]: {
        box: 'h-5 w-5',
        font: 'text-sm',
        icon: 'h-3.5 w-3.5',
        padding: 'pt-0',
    },
} as const;

const textVariants = {
    description: (variant: CheckboxSizeType) =>
        cn(sizeStyles[variant].font, 'text-midnight opacity-80 group-has-[:disabled]:text-midnight-disabled-text'),

    label: (variant: CheckboxSizeType) =>
        cn(
            sizeStyles[variant].font,
            sizeStyles[variant].padding,
            'text-midnight group-has-[:disabled]:text-midnight-disabled-text',
        ),
};

/**
 * A customizable checkbox component with support for sizes, labels,
 * descriptions, and disabled state.
 *
 * @param id - **Required.** Unique identifier for the checkbox input.
 * @param size - Visual size of the checkbox. Defaults to `'lg'` {@link CheckboxSizeType}.
 * @param label - Optional label displayed next to the checkbox.
 * @param description - Optional helper text displayed below the label.
 * @param disabled - Whether the checkbox is disabled.
 * @param labelClassName - Additional CSS classes to merge with default label styles.
 * @param descriptionClassName - Additional CSS classes to merge with default description styles.
 * @param className - Additional CSS classes to merge with default styles.
 * @param ...props - Additional HTML input attributes.
 *
 * @example
 * <Checkbox id={'term'} label="Accept terms" />
 *
 * @example
 * <Checkbox
 *   id="newsletter"
 *   size={CheckboxSize.LARGE}
 *   label="Subscribe to newsletter"
 *   description="We'll only send relevant content."
 * />
 *
 * @example
 * <Checkbox
 *   id={'agree'}
 *   size={CheckboxSize.MEDIUM}
 *   label="I agree"
 *   disabled
 * />
 */

export const Checkbox: React.FC<CheckboxProps> = ({
    className,
    description,
    descriptionClassName,
    eventPreventDefault,
    eventStopPropagation,
    id,
    label,
    labelClassName,
    onClick,
    size = CheckboxSize.MEDIUM,
    ...props
}) => {
    const labelId = `${id}-label`;
    const descriptionId = `${id}-description`;

    const sizeData = sizeStyles[size];

    const defaultClasses = cn(
        'peer block border rounded-sm border-grey bg-white appearance-none focus:outline-none focus:ring-2',
        'checked:border-2 checked:bg-midnight checked:border-midnight cursor-pointer disabled:cursor-not-allowed disabled:border-midnight-disabled checked:disabled:bg-midnight-disabled',
        sizeData.box,
    );

    return (
        <label
            className={cn('group flex cursor-pointer flex-col gap-1.5  select-none has-[:disabled]:cursor-not-allowed')}
            htmlFor={id}
        >
            <div className="flex items-start gap-2">
                <div className="relative">
                    <input
                        type="checkbox"
                        id={id}
                        className={cn(defaultClasses, className)}
                        disabled={props.disabled}
                        aria-labelledby={`${id}-label`}
                        aria-checked={props.checked}
                        onClick={(e) => {
                            handleEventBehavior(e, { eventPreventDefault, eventStopPropagation });
                            onClick?.(e);
                        }}
                        {...props}
                    />

                    <LuCheck
                        className={cn(
                            'text-white pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ',
                            sizeData.icon,
                            'opacity-0 peer-checked:opacity-100 transition-opacity',
                        )}
                    />
                </div>
                <div className="flex flex-col">
                    {label && (
                        <span id={labelId} className={cn(textVariants.label(size), labelClassName)}>
                            {label}
                        </span>
                    )}
                    {description && (
                        <span id={descriptionId} className={cn(textVariants.description(size), descriptionClassName)}>
                            {description}
                        </span>
                    )}
                </div>
            </div>
        </label>
    );
};
